import {
  childrenSelector,
  isPrimitive,
  isPrimitiveRecordArray,
  valueSelector,
} from '@smartsheet-dataflow/formjs';
import { componentSelector } from '@smartsheet-dataflow/formjs-react';
import isNil from 'lodash/fp/isNil';
import { BridgeFormJSState } from '../Types';
import {
  AllParamModel,
  EnumParamModel,
  GroupParamModel,
} from './ParamModelTypes';
import { ParamSchemaTypes } from './ParamSchemaTypes';

export const getParamId = (inputId: string) =>
  inputId && inputId.slice(inputId.lastIndexOf('.') + 1);

export const isTextParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.TEXT => type === 'TEXT';
export const isStringParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.STRING => type === 'STRING';
export const isNumberParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.NUMBER => type === 'NUMBER';
export const isBooleanParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.BOOLEAN => type === 'BOOLEAN';
export const isDerivedParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.DERIVED => type === 'DERIVED';
export const isDerivedJSONParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes['DERIVED-JSON'] => type === 'DERIVED-JSON';
export const isJSONParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.JSON => type === 'JSON';
export const isEnumParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.ENUM => type === 'ENUM';
export const isWorkflowParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes['BRIDGE.WORKFLOW'] =>
  type === 'BRIDGE.WORKFLOW';
export const isHtmlLinkParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes['HTML.LINK'] => type === 'HTML.LINK';
export const isMapParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.MAP => type === 'MAP';
export const isGroupParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.PARAM => type === 'GROUP';
export const isGroupArrayParamModelType = (
  type: ParamSchemaTypes | string
): type is typeof ParamSchemaTypes.PARAM => type === 'GROUP_ARRAY';

export const isStringArray = (value: any): value is string[] =>
  Array.isArray(value) &&
  value.reduce((prev, curr) => prev && typeof curr === 'string', true);

export const getModelFromParam = (
  inputId: string,
  state: BridgeFormJSState
): AllParamModel | undefined => {
  const paramId = getParamId(inputId);
  if (paramId !== undefined) {
    const type = componentSelector(inputId)(state).toUpperCase();
    const value = valueSelector(inputId)(state);

    if (isPrimitive(value) || isStringArray(value)) {
      return value;
    }

    // MAP
    if (isMapParamModelType(type) && isPrimitiveRecordArray(value)) {
      return value.reduce(
        (acc, record) => ({ ...acc, [record.key]: record.value }),
        {}
      );
    }

    // ENUM
    if (isEnumParamModelType(type) && typeof value === 'string') {
      return getModelFromEnumParam(value, state);
    }

    // WORKFLOW
    if (isWorkflowParamModelType(type) && typeof value === 'string') {
      return getModelFromEnumParam(value, state);
    }

    // GROUP / PARAM
    if (isGroupParamModelType(type)) {
      return getModelFromGroupParam(inputId, state);
    }

    // GROUP_ARRAY
    if (isGroupArrayParamModelType(type)) {
      const children = childrenSelector(inputId)(state) || [];
      return children.map(childId => getModelFromGroupParam(childId, state));
    }
  }
};

export const getModelFromEnumParam = (
  selected: string,
  state: BridgeFormJSState
): EnumParamModel | undefined => {
  const value = valueSelector(selected)(state);
  if (typeof value === 'string') {
    return value;
  }
};

export const getModelFromGroupParam = (
  inputId: string,
  state: BridgeFormJSState
): GroupParamModel => {
  const children = childrenSelector(inputId)(state);
  return children.reduce(
    reduceModelFromGroupParam(inputId, state),
    {} as GroupParamModel
  );
};

export const reduceModelFromGroupParam =
  (inputId: string, state: BridgeFormJSState) =>
  (model: GroupParamModel, childId: string): GroupParamModel => {
    const value = getModelFromParam(childId, state);
    if (isNil(value)) {
      return model;
    }
    return {
      ...model,
      [childId.replace(`${inputId}.`, '')]: value,
    };
  };

export const reduceModelFromStringArrayParam =
  (state: BridgeFormJSState) =>
  (model: string[], childId: string): string[] => {
    const value = valueSelector(childId)(state) as string;
    if (isNil(value)) {
      return model;
    }
    return [...model, value];
  };
